import { type ReactNode, useState } from 'react';

interface ExternalEmbedProps {
    label: string;
    className?: string;
    title?: string;
    posterSrc?: string;
    variant?: 'video' | 'social';
    children: ReactNode;
}

export default function ExternalEmbed({
    label,
    title = label,
    posterSrc = '/images/hero/1.webp',
    variant = 'video',
    className = 'aspect-video',
    children,
}: ExternalEmbedProps) {
    const [isLoaded, setIsLoaded] = useState(false);

    return (
        <div className={isLoaded || variant === 'video' ? className : undefined}>
            {isLoaded ? (
                children
            ) : variant === 'social' ? (
                <div className="bg-card">
                    <div className="relative h-32 overflow-hidden bg-[#063f22]">
                        <img src="/images/hero/1.webp" alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" />
                        <div className="absolute inset-0 bg-linear-to-t from-[#063f22]/80 to-transparent" />
                        <span className="absolute bottom-4 left-5 text-xs font-bold tracking-widest text-white uppercase">Our community</span>
                    </div>
                    <div className="flex flex-col gap-4 p-5">
                        <div className="flex items-center gap-3">
                            <img src="/images/logo.webp" alt="" width="48" height="48" className="size-12 object-contain" loading="lazy" />
                            <div>
                                <h3 className="text-foreground text-base font-semibold">Northeastern College</h3>
                                <p className="text-muted-foreground text-sm">@NCnianAko</p>
                            </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                            Campus stories, student highlights, and the latest announcements from NC.
                        </p>
                        <button
                            type="button"
                            onClick={() => setIsLoaded(true)}
                            className="rounded-xl bg-[#0A7A35] px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#08632b]"
                        >
                            {label}
                        </button>
                        <a
                            href="https://www.facebook.com/NCnianAko"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-center text-sm font-medium text-[#0A7A35] hover:underline"
                        >
                            Visit our Facebook page ↗
                        </a>
                    </div>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => setIsLoaded(true)}
                    aria-label={label}
                    className="group relative flex h-full w-full flex-col items-center justify-center gap-4 overflow-hidden bg-[#063f22] text-white focus-visible:outline-4 focus-visible:outline-offset-[-4px] focus-visible:outline-white"
                >
                    <img
                        src={posterSrc}
                        onError={(event) => {
                            if (!event.currentTarget.src.endsWith('/images/hero/1.webp')) {
                                event.currentTarget.src = '/images/hero/1.webp';
                            }
                        }}
                        alt=""
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105 motion-reduce:transition-none"
                        loading="lazy"
                        decoding="async"
                    />
                    <div className="absolute inset-0 bg-linear-to-t from-black/90 via-black/50 to-black/20" />
                    <span className="relative flex size-16 items-center justify-center rounded-full border border-white/50 bg-white/15 backdrop-blur-sm transition-transform group-hover:scale-110 motion-reduce:transition-none">
                        <svg viewBox="0 0 24 24" className="ml-1 size-7 fill-white" aria-hidden="true">
                            <path d="M8 5v14l11-7z" />
                        </svg>
                    </span>
                    <span className="relative px-6 text-center text-xl font-semibold sm:text-2xl">{title}</span>
                    <span className="relative text-sm text-white/90">Watch video</span>
                    <span className="absolute top-4 left-4 rounded-full bg-black/40 px-3 py-1 text-xs font-medium text-white">
                        Northeastern College
                    </span>
                </button>
            )}
        </div>
    );
}
